import { ReactElement } from 'react';
import { Stack, Typography, Chip, IconButton, Popover, Button, CircularProgress, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState, useEffect } from "react";
import { useHubstripe } from '../../../hooks/useHubstripe'
import { t } from 'i18next';
import { THubstripeCustomer } from "../../../shared/types/hubstripeCustomer.type";
import SvgIconMasterCard from '../../../assets/icons/billing/SvgIconMasterCard';
import SvgIconVisa from '../../../assets/icons/billing/SvgIconVisa';
import SvgIconAmericanExpress from '../../../assets/icons/billing/SvgIconAmericanExpress';
import SvgIconCB from '../../../assets/icons/billing/SvgIconCB';
import SvgIconStripe from '../../../assets/icons/billing/SvgIconStripe';

type BillingPaymentMethodsItemsRowProps = {
    customer: THubstripeCustomer | null;
    paymentMethod: any | null;
    onPaymentMethodsChange: () => void;
    loadingPaymentMethods: boolean;
}

function BillingPaymentMethodsItemsRow({ customer, paymentMethod, onPaymentMethodsChange, loadingPaymentMethods }: BillingPaymentMethodsItemsRowProps) {

    const { updatePaymentMethodForCustomerId, deletePaymentMethodForCustomerId } = useHubstripe()

    const [anchorEl, setAnchorEl] = useState(null)
    const [loading, setLoading] = useState(false)

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSetAsDefault = (event: any) => {
        handleClose();
        setLoading(true);
        onPaymentMethodsChange()


        updatePaymentMethodForCustomerId.mutateAsync({
            customerId: customer?.id ?? '',
            paymentMethodId: event.target.getAttribute('value'),
        })
            .then(() => {
                setLoading(false);
                onPaymentMethodsChange()
            }).catch(err => {
                console.log({ err });
            })
    }

    const handleDelete = (event: any) => {
        handleClose();
        setLoading(true);
        onPaymentMethodsChange()

        deletePaymentMethodForCustomerId.mutateAsync({
            customerId: customer?.id ?? '',
            paymentMethodId: event.target.getAttribute('value'),
        })
            .then(() => {
                setLoading(false);
                onPaymentMethodsChange()
            }).catch(err => {
                console.log({ err });
            })
    }

    useEffect(() => {
        setLoading(loadingPaymentMethods);
    }, [loadingPaymentMethods]);

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    var avatarIcon: ReactElement = <></>
    var name = ""
    var digit = ""

    switch (paymentMethod.type) {
        case "card":
            switch (paymentMethod.card.brand) {
                case "mastercard":
                    avatarIcon = <SvgIconMasterCard fontSize="large" />
                    name = "billing:mastercard"
                    digit = "••• " + paymentMethod.card.last4
                    break;
                case "visa":
                    avatarIcon = <SvgIconVisa fontSize="large" />
                    name = "billing:visa"
                    digit = "••• " + paymentMethod.card.last4
                    break;
                case "amex":
                    avatarIcon = <SvgIconAmericanExpress fontSize="large" />
                    name = "billing:american_express"
                    digit = "••• " + paymentMethod.card.last4
                    break;
                default:
                    avatarIcon = <SvgIconCB fontSize="large" />
                    name = "billing:card"
                    digit = "••• " + paymentMethod.card.last4
                    break;
            }
            break;
        case "sepa_debit":
            avatarIcon = <SvgIconMasterCard fontSize="large" />
            name = "billing:sepa"
            digit = "••• " + paymentMethod.sepa_debit.last4
            break;
        case "link":
            avatarIcon = <SvgIconStripe fontSize="large" />
            name = "billing:link_payment"
            digit = ""
            break;
        default:
            break;
    }

    return (
        <Stack direction={"row"} spacing={1} alignItems="flex-start" justifyContent="space-between">

            <Stack direction="row" spacing={1}>
                {avatarIcon}
                <Stack spacing={0}>
                    <Stack direction={{ md: "row", sm: 'column' }} spacing={1} flexWrap={'wrap'}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {t(name)} {digit}
                        </Typography>
                        {customer && customer.invoice_settings && paymentMethod.id == customer.invoice_settings.default_payment_method && (
                            <Chip label={t("billing:default")} color={"info"} size="small" />
                        )}
                        {/*  */}
                    </Stack>
                    {paymentMethod.type === "card" && (
                        <Typography variant="body2" sx={{ color: "#606060" }}>
                            {t("billing:expires")} {("0" + paymentMethod.card.exp_month).slice(-2)}/{paymentMethod.card.exp_year}
                        </Typography>
                    )}

                </Stack >

            </Stack>

            <IconButton aria-label="delete" disabled={loading}>
                {loading && <CircularProgress size="1rem" color="inherit" />}
                {!loading && <MoreVertIcon onClick={handleClick} />}
            </IconButton>

            <Menu
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <MenuItem onClick={handleSetAsDefault} value={paymentMethod.id}> {t("billing:set_default")}</MenuItem>
                <MenuItem onClick={handleDelete} value={paymentMethod.id} >{t("billing:delete")}</MenuItem>

            </Menu>
        </Stack>

    );
}

export default BillingPaymentMethodsItemsRow;
